"use client";

import Cookies from "js-cookie";
import { LDProvider, useLDClient } from "launchdarkly-react-client-sdk";
import {
  createContext,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  useContext,
  useEffect,
  useState,
} from "react";

interface AuthenticatedLaunchDarklyContextReadyValue {
  isReady: boolean;
  setIsReady: Dispatch<SetStateAction<boolean>>;
}

const AuthenticatedLaunchDarklyContextReadyContext = createContext<AuthenticatedLaunchDarklyContextReadyValue | null>(
  null,
);

export function AuthenticatedLaunchDarklyContextReadyProvider({ children }: { children: ReactNode }) {
  const [isReady, setIsReady] = useState(false);

  return (
    <AuthenticatedLaunchDarklyContextReadyContext.Provider value={{ isReady, setIsReady }}>
      {children}
    </AuthenticatedLaunchDarklyContextReadyContext.Provider>
  );
}

export function useAuthenticatedLaunchDarklyContextReady(organizationScopeId?: string) {
  const isReady = useContext(AuthenticatedLaunchDarklyContextReadyContext)?.isReady ?? true;
  const ldClient = useLDClient();
  if (!isReady || !organizationScopeId || !ldClient) return isReady;

  const context = ldClient.getContext();
  return (
    !!context && "kind" in context && context.kind === "multi" && context.organization?.key === organizationScopeId
  );
}

export function useSetAuthenticatedLaunchDarklyContextReady() {
  return useContext(AuthenticatedLaunchDarklyContextReadyContext)?.setIsReady;
}

interface LaunchDarklyProviderProps {
  children: ReactNode;
  context:
    | {
        kind: "multi";
        user: {
          key: string;
          email?: string;
          anonymous?: boolean;
        };
        organization?: {
          key: string;
          name?: string;
        };
        team?: {
          key: string;
          name?: string;
        };
      }
    | {
        kind: string;
        key: string;
        email?: string;
        custom?: {
          organizationId?: string;
          organizationName?: string;
          teamId?: string;
          teamName?: string;
        };
      };
  clientSideID: string;
}

/**
 * Generate a random 16-digit key for anonymous users
 * This allows LaunchDarkly to bucket different anonymous users for A/B testing
 */
const generateAnonymousKey = (): string => {
  const digits = "0123456789";
  let result = "";
  for (let i = 0; i < 16; i++) {
    result += digits.charAt(Math.floor(Math.random() * digits.length));
  }
  return result;
};

/**
 * Get or create a persistent anonymous user key in a client-side cookie
 * Stored in a cookie to maintain consistency across sessions for A/B testing
 */
const getOrCreateAnonymousKey = (): string => {
  const cookieName = "ld_anonymous_key";

  // Check for existing cookie
  const existingKey = Cookies.get(cookieName);

  if (existingKey) {
    return existingKey;
  }

  // Generate new key and store in cookie (1 year expiration)
  const newKey = generateAnonymousKey();
  Cookies.set(cookieName, newKey, {
    expires: 365, // 1 year
    path: "/",
    sameSite: "lax",
    secure: process.env.NODE_ENV === "production",
  });

  return newKey;
};

// Track whether LD has ever initialized in this browser session.
// On first load, we block rendering until flags are ready (prevents pop-in).
// On subsequent remounts (e.g. org switch), we skip blocking since the SDK
// serves cached flags immediately — a blank screen is worse than a brief pop-in.
let hasEverInitialized = false;

function LaunchDarklyLoadingGate({ children }: { children: ReactNode }) {
  const ldClient = useLDClient();
  const [isReady, setIsReady] = useState(hasEverInitialized);
  const [initTimeout, setInitTimeout] = useState(false);
  const [anonymousKeyReplaced, setAnonymousKeyReplaced] = useState(false);

  // Handle anonymous user key generation and storage client-side
  // This must complete BEFORE we wait for initialization to prevent double-init flicker
  useEffect(() => {
    if (!ldClient) return;

    const currentContext = ldClient.getContext() as
      | { kind?: string; user?: { key: string; anonymous?: boolean } }
      | undefined;

    // If we have an anonymous temp key, replace it with a persistent one
    if (currentContext?.user?.key === "anonymous-temp" && currentContext?.user?.anonymous) {
      const persistentKey = getOrCreateAnonymousKey();
      ldClient
        .identify({
          kind: currentContext.kind || "multi",
          ...currentContext,
          user: {
            ...currentContext.user,
            key: persistentKey,
          },
        })
        .then(() => {
          setAnonymousKeyReplaced(true);
        })
        .catch((error) => {
          console.error("[LaunchDarkly] Failed to replace anonymous key:", error);
          // Still mark as replaced to avoid blocking
          setAnonymousKeyReplaced(true);
        });
    } else {
      // Not an anonymous temp user, no need to replace key
      setAnonymousKeyReplaced(true);
    }
  }, [ldClient]);

  useEffect(() => {
    if (!ldClient || !anonymousKeyReplaced) return;

    // Set a timeout to prevent infinite blocking
    const timeoutId = setTimeout(() => {
      console.warn("[LaunchDarkly] Initialization timeout - proceeding without waiting for flags");
      setInitTimeout(true);
    }, 5000); // 5 second timeout

    const checkReady = async () => {
      try {
        await ldClient.waitForInitialization();
        clearTimeout(timeoutId);
        hasEverInitialized = true;
        setIsReady(true);

        // Track initialization event
        ldClient.track("launchdarkly-client-initialized", {
          timestamp: new Date().toISOString(),
          environment: process.env.NODE_ENV,
        });
      } catch (error) {
        console.error("[LaunchDarkly] Failed to initialize:", error);
        clearTimeout(timeoutId);
        hasEverInitialized = true;
        setIsReady(true); // Set to true anyway to avoid blocking the UI
      }
    };

    checkReady();

    return () => clearTimeout(timeoutId);
  }, [ldClient, anonymousKeyReplaced]);

  // Wait for initialization on first load to prevent feature flag pop-in.
  // On subsequent remounts (org switch), skip blocking — the SDK serves
  // cached flags immediately and a blank screen is worse than a brief pop-in.
  const isLoading = !isReady && !initTimeout;

  if (isLoading) {
    return null;
  }

  return <>{children}</>;
}

export function LaunchDarklyProvider({ children, context, clientSideID }: LaunchDarklyProviderProps) {
  return (
    <LDProvider clientSideID={clientSideID} context={context} reactOptions={{ useCamelCaseFlagKeys: false }}>
      <LaunchDarklyLoadingGate>{children}</LaunchDarklyLoadingGate>
    </LDProvider>
  );
}
